import React, { useEffect, useReducer, useState } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import Typography from '@mui/material/Typography';
import {
  AssessmentQuestion,
  AssessmentQuestionType,
} from './AssessmentQuestionType';
import { StepContent, StepLabel } from '@mui/material';
import { produce } from 'immer';
import AssessmentQuestionStepRenderer from './AssessmentQuestionStepRenderer';
import {
  AssessmentQuestionAction,
  AssessmentQuestionActionType,
} from './AssessmentQuestionAction';
import { useParams } from 'react-router-dom';
import APICaller, { MethodType } from '../common/APICaller';
import { useNavigate } from 'react-router-dom';

const questionsReducer = (
  state: AssessmentQuestion[],
  action: AssessmentQuestionAction,
) => {
  if (action.type === AssessmentQuestionActionType.Init) {
    const questions: AssessmentQuestion[] = [];
    action.payload.data.forEach(q => {
      switch (q.type) {
        case AssessmentQuestionType.TEXT:
          questions.push({
            id: q.id,
            type: q.type,
            description: q.text,
            answer:
              q.answer.length === 0 ? '' : q.answer[0].textInputAnswer.text,
            answerId: q.answer.length > 0 ? q.answer[0].id : null,
            maxGrade: q.maxGrade,
            hasQuestionFile: q.file != null,
          });
          break;
        case AssessmentQuestionType.MULTIPLE_CHOICE:
        case AssessmentQuestionType.SINGLE_CHOICE:
          const choices: {
            [choice_id: number]: {
              text: string;
              chosen: boolean;
            };
          } = {};
          const hasAnswered = q.answer.length > 0;
          const selectedAnswer: number[] = hasAnswered
            ? q.answer[0].possibleChoice.map((d: { id: number }) => d.id)
            : [];
          q.possibleChoice.forEach((choice: { id: number; option: string }) => {
            choices[choice.id] = {
              text: choice.option,
              chosen: !hasAnswered ? false : selectedAnswer.includes(choice.id),
            };
          });
          questions.push({
            id: q.id,
            type: q.type,
            description: q.text,
            answerId: q.answer.length > 0 ? q.answer[0].id : null,
            answer: choices,
            maxGrade: q.maxGrade,
            hasQuestionFile: q.file != null,
          });
          break;
        case AssessmentQuestionType.FILE_UPLOAD:
          questions.push({
            id: q.id,
            type: q.type,
            description: q.text,
            answer: q.answer.length === 0 ? '' : q.answer[0],
            answerId: q.answer.length > 0 ? q.answer[0].id : null,
            maxGrade: q.maxGrade,
            hasQuestionFile: q.file != null,
          });
          break;
      }
    });
    state = questions;
  } else if (
    action.type === AssessmentQuestionActionType.UpdateMultipleChoice
  ) {
    const { answer, question_index, answerId } = action.payload;
    Object.keys(state[question_index].answer).forEach(choie_id => {
      const choice = state[question_index].answer[+choie_id];
      if (typeof choice !== 'string') {
        if (answer.includes(+choie_id)) {
          choice.chosen = true;
        } else {
          choice.chosen = false;
        }
      }
    });
    if (answerId) {
      state[question_index].answerId = action.payload.answerId;
    }
  } else {
    const { question_index, answer, answerId } = action.payload;
    state[question_index].answer = answer;
    if (answerId) {
      state[question_index].answerId = action.payload.answerId;
    }
  }
  return state;
};
const AssessmentStepper: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [questions, dispatch] = useReducer(produce(questionsReducer), []);
  const { jobId, assessmentId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    APICaller(
      `/application/assessments/${assessmentId}/questions-answers`,
      MethodType.GET,
    )
      .then(res => {
        console.log(res);
        if (!res.errorMessage) {
          dispatch({
            type: AssessmentQuestionActionType.Init,
            payload: { data: res },
          });
        }
      })
      .catch(e => {
        console.log(e);
      });
  }, []);
  const goNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const goBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleSubmit = () => {
    // Auto-save now handles saving before submit at question level
    // No need to validate answerId here anymore
    console.log('Submit Assessment');
    APICaller(
      `/application/assessments/${assessmentId}/submit`,
      MethodType.PUT,
    )
      .then(res => {
        console.log(res);
        if (!res.errorMessage) {
          navigate(`/assessments/${jobId}`)
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  return (
    <Box sx={{ ml: '10%', mt: '5vh' }}>
      <Stepper activeStep={activeStep} orientation="vertical">
        {questions.map((q, index) => (
          <Step key={index}>
            <StepLabel
              optional={
                index === questions.length - 1 ? (
                  <Typography variant="caption">Last step</Typography>
                ) : null
              }
              error={activeStep > index && !q.answerId}
              onClick={() => setActiveStep(index)}
              sx={{ cursor: 'pointer !important' }}
            >
              {`Q${index + 1} (${q.maxGrade} Mark)`}
            </StepLabel>
            <StepContent>
              <AssessmentQuestionStepRenderer
                data={q}
                index={index}
                totalCount={questions.length}
                goNext={goNext}
                goBack={goBack}
                handleSubmit={handleSubmit}
                dispatch={dispatch}
              />
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default AssessmentStepper;
